import React, {useCallback, useEffect} from 'react';
import {renderRichText} from 'gatsby-source-contentful/rich-text';
import {BLOCKS, INLINES} from '@contentful/rich-text-types';
import {styled, useStyletron} from 'baseui';
import {graphql, Link, useStaticQuery, navigate} from 'gatsby';
import {GatsbyImage} from 'gatsby-plugin-image';
import Icon from '@mdi/react';
import {mdiArrowLeft} from '@mdi/js';
import Layout from '../components/Layout';
import SEO from '../components/Seo';
import {generateSummaryText} from '../utils/blog';
import {createIdFromTitle} from '../utils/anchorTitles';
import {useHover} from '../utils/hooks';
import {handleMixpanelAnalytics, MixpanelEvents} from '../utils/events';
import {getPathPrefixForNewsAndCommunity} from '../utils/utils';

type Props = {
  pageContext: {
    image: any;
    title: string;
    intro: string;
    datePublished: string;
    content: any;
    slug: string;
    contentSection: string;
  };
};

const TEXT_MAX_WIDTH = '800px';

const LIST_MAX_WIDTH = '790px';

const BackButtonWrapper = styled('div', () => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  cursor: 'pointer',
  maxWidth: TEXT_MAX_WIDTH,
}));

const BackButtonText = styled<'p', {$backHovered: boolean}>(
  'p',
  ({$theme, $backHovered}) => ({
    fontSize: '18px',
    fontWeight: 700,
    margin: '2px 0 0 16px',
    textDecoration: 'none',
    transition: '500ms ease',
    borderBottom: $backHovered
      ? `1px solid ${$theme.colors.black}`
      : '1px solid transparent',
  })
);

const Paragraph = styled('p', () => ({
  fontSize: '16px',
  lineHeight: '26px',
  width: '100%',
  maxWidth: TEXT_MAX_WIDTH,
}));

const StyledLink = styled(Link, ({$theme}) => ({
  color: $theme.colors.primary,
  textDecoration: 'underline',
  fontWeight: 700,
  margin: '2px 0',
}));

const StyledA = styled('a', ({$theme}) => ({
  color: $theme.colors.primary,
  textDecoration: 'underline',
  fontWeight: 700,
  margin: '2px 0',
}));

const ContentWrapper = styled('div', () => ({
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Title = styled('h1', ({$theme}) => ({
  marginTop: '64px',
  width: '100%',
  maxWidth: TEXT_MAX_WIDTH,
  fontSize: '46px',
  [$theme.mediaQuery.small]: {
    marginTop: '96px',
  },
}));

const MetaSummary = styled('p', () => ({
  margin: '8px 0',
  fontSize: '16px',
  maxWidth: TEXT_MAX_WIDTH,
  width: '100%',
}));

const Intro = styled('p', ({$theme}) => ({
  margin: '16px 0 24px 0',
  fontSize: '18px',
  color: $theme.colors.primary,
  fontWeight: 700,
  lineHeight: '32px',
  width: '100%',
  maxWidth: TEXT_MAX_WIDTH,
}));

const VideoWrapper = styled('div', () => ({
  width: '100%',
  flexDirection: 'column',
  display: 'flex',
}));

const ImageWrapper = styled('div', () => ({
  width: '100%',
  maxWidth: TEXT_MAX_WIDTH,
  margin: '32px 0',
}));

const HeaderImageWrapper = styled('div', () => ({
  width: '100%',
  margin: '0 0 32px 0',
  maxWidth: TEXT_MAX_WIDTH,
}));

const ImageCaption = styled('p', ({$theme}) => ({
  fontSize: '14px',
  color: 'rgb(117, 117, 117)',
  maxWidth: TEXT_MAX_WIDTH,
  width: '80%',
  margin: '8px auto 42px auto',
  textAlign: 'center',
  [$theme.mediaQuery.small]: {
    margin: '8px auto 16px auto',
    width: '100%',
  },
}));

const Heading2 = styled('h2', ({$theme}) => ({
  marginTop: '72px',
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  maxWidth: TEXT_MAX_WIDTH,
  transition: '500ms ease',
  '@media (hover: hover)': {
    transform: 'translateX(-36px)',
    '::before': {
      opacity: 0,
    },
    [$theme.mediaQuery.small]: {
      transform: 'translateX(0px)',
    },
  },
  ':hover': {
    cursor: 'pointer',
    transform: 'translateX(0)',
    '::before': {
      opacity: 1,
    },
  },
  '::before': {
    marginRight: '8px',
    fontFamily: 'Material Icons',
    content: "'link'",
    transition: '500ms ease',
    WebkitFontFeatureSettings: 'liga',
    [$theme.mediaQuery.small]: {
      display: 'none',
    },
  },
  ':active': {
    '::before': {
      transition: 'none',
      opacity: 0,
    },
  },
}));

const Video = styled('video', () => ({
  width: '100%',
  height: 'auto',
}));

const OrderedListFullWidth = styled('ol', () => ({
  width: 'calc(99% - 42px)',
  marginLeft: '42px',
  maxWidth: LIST_MAX_WIDTH,
}));

const UnOrderedListFullWidth = styled('ul', () => ({
  width: 'calc(99% - 42px)',
  marginLeft: '42px',
  maxWidth: TEXT_MAX_WIDTH,
}));

const renderOptions = {
  renderNode: {
    [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => (
      <UnOrderedListFullWidth>{children}</UnOrderedListFullWidth>
    ),
    [BLOCKS.OL_LIST]: (node: any, children: React.ReactNode) => (
      <OrderedListFullWidth>{children}</OrderedListFullWidth>
    ),
    [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
      if (
        Array.isArray(children) &&
        children[0] &&
        children[0].props &&
        children[0].props.isvideo
      ) {
        return <div>{children}</div>;
      }

      return <Paragraph>{children}</Paragraph>;
    },
    [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => {
      const title = Array.isArray(children)
        ? children[0].props?.children || children[0]
        : null;

      const copyAnchor = async () => {
        if (title !== undefined) {
          const anchor = `${window.location.protocol}//${window.location.host}${
            window.location.pathname
          }#${createIdFromTitle(title)}`;
          await navigator.clipboard.writeText(anchor);
        }
      };

      return (
        <Heading2
          onClick={copyAnchor}
          id={title !== undefined ? createIdFromTitle(title) : ''}
        >
          {children}
        </Heading2>
      );
    },
    [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
      if (node.data.uri.includes('mailto') || node.data.uri.includes('http')) {
        return <StyledA href={node.data.uri}>{children}</StyledA>;
      }
      return <StyledLink to={node.data.uri}>{children}</StyledLink>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const {gatsbyImageData, description, mimeType, file} = node.data.target;

      if (mimeType === 'video/mp4') {
        return (
          <VideoWrapper>
            <Video muted loop playsInline autoPlay preload='auto'>
              <source src={file.url} type='video/mp4' />
            </Video>
            <ImageCaption>{description}</ImageCaption>
          </VideoWrapper>
        );
      }

      if (!gatsbyImageData) {
        // asset is not an image
        return null;
      }

      return (
        <ImageWrapper>
          <GatsbyImage
            alt={description}
            image={gatsbyImageData}
            style={{
              width: '100%',
            }}
            imgStyle={{
              objectFit: 'contain',
            }}
          />
          <ImageCaption>{description}</ImageCaption>
        </ImageWrapper>
      );
    },
  },
};

const BlogEntry = function ({pageContext}: Props) {
  const [css, theme] = useStyletron();
  const [backHoverRef, backHovered] = useHover<HTMLDivElement>();
  const {title, intro, datePublished, content, image, slug, contentSection} =
    pageContext;
  const data = useStaticQuery(graphql`
    query BlogEntryQuery {
      contentfulNewsAndCommunity {
        readTimeSummary
        allArticles
      }
    }
  `);

  const {readTimeSummary, allArticles} = data.contentfulNewsAndCommunity;

  useEffect(() => {
    handleMixpanelAnalytics(MixpanelEvents.BLOG_POST_ENTERED, {
      blog_title: title,
      slug,
    });
  }, []);

  const onNavigateToBlogPress = useCallback(() => {
    const path = getPathPrefixForNewsAndCommunity(contentSection);

    navigate(`/${path}`);
  }, [contentSection]);

  return (
    <Layout showProductSelectInFooter>
      <SEO title={title} description={intro} />
      <ContentWrapper>
        <BackButtonWrapper onClick={onNavigateToBlogPress} ref={backHoverRef}>
          <Icon size={1} path={mdiArrowLeft} color={theme.colors.black} />
          <BackButtonText $backHovered={backHovered}>
            {allArticles}
          </BackButtonText>
        </BackButtonWrapper>
        <Title>{title}</Title>
        <MetaSummary>
          {generateSummaryText(content.raw, datePublished, readTimeSummary)}
        </MetaSummary>
        <Intro>{intro}</Intro>
        <HeaderImageWrapper>
          <GatsbyImage
            loading='lazy'
            image={image.gatsbyImageData}
            alt={image.description}
            className={css({
              width: '100%',
              height: '100%',
            })}
            imgStyle={{
              objectFit: 'contain',
            }}
          />
        </HeaderImageWrapper>
        {renderRichText(content, renderOptions)}
      </ContentWrapper>
    </Layout>
  );
};

export default BlogEntry;
